/**
 * Date/Time Utility Functions
 * Provides relative and absolute time formatting with i18n support
 */

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-tw';

// Enable the relativeTime plugin
dayjs.extend(relativeTime);

/**
 * Format a date as relative time (e.g., "2 hours ago")
 * @param date - ISO date string, Date object, or timestamp
 * @param locale - Locale string ('en' or 'zh')
 * @returns Relative time string
 */
export function formatRelativeTime(
  date: string | Date | number | null | undefined,
  locale: string = 'en'
): string {
  if (!date) return '-';

  try {
    const dayjsLocale = locale.startsWith('zh') ? 'zh-tw' : 'en';
    return dayjs(date).locale(dayjsLocale).fromNow();
  } catch {
    return '-';
  }
}

/**
 * Format a date as absolute time (e.g., "2025-12-04 14:30:00")
 * @param date - ISO date string, Date object, or timestamp
 * @param locale - Locale string ('en' or 'zh')
 * @param showSeconds - Whether to include seconds in the output
 * @returns Absolute time string
 */
export function formatAbsoluteTime(
  date: string | Date | number | null | undefined,
  _locale: string = 'en',
  showSeconds: boolean = true
): string {
  if (!date) return '-';

  try {
    const format = showSeconds ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD HH:mm';
    return dayjs(date).format(format);
  } catch {
    return '-';
  }
}

/**
 * Format a date for display (e.g., "04/12/2025")
 * @param date - ISO date string, Date object, or timestamp
 * @returns Formatted date string
 */
export function formatDate(
  date: string | Date | number | null | undefined
): string {
  if (!date) return '-';

  try {
    return dayjs(date).format('DD/MM/YYYY');
  } catch {
    return '-';
  }
}

/**
 * Get both relative and absolute time for tooltip display
 * @param date - ISO date string, Date object, or timestamp
 * @param locale - Locale string ('en' or 'zh')
 * @returns Object with relative and absolute time strings
 */
export function getTimestampDisplay(
  date: string | Date | number | null | undefined,
  locale: string = 'en'
): { relative: string; absolute: string } {
  return {
    relative: formatRelativeTime(date, locale),
    absolute: formatAbsoluteTime(date, locale),
  };
}
